import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from './ui/select';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Upload, X, Plus, Loader2, AlertTriangle, CheckCircle, XCircle, Check } from 'lucide-react';
import axios from 'axios';
import { useListingSeoScore, calculateListingSeoScore } from '../hooks/useSeoScore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ListingForm = ({ 
  initialData = null,
  onSubmit, 
  submitting = false, 
  error = '',
  submitButtonText,
  loadingButtonText,
  token,
  isAdmin = false
}) => {
  const { t } = useTranslation();
  const [attributes, setAttributes] = useState({
    categories: [],
    cities: [],
    genders: [],
    services: [],
    eye_colors: [],
    hair_colors: [],
    body_types: [],
    heights: []
  });

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    title: initialData?.title || '',
    category: initialData?.category || '',
    city: initialData?.city || '',
    description: initialData?.description || '',
    skills: initialData?.skills || [],
    phone: initialData?.phone || '',
    whatsapp: initialData?.whatsapp || '',
    available: initialData?.available ?? true,
    images: initialData?.images || [],
    images_data: initialData?.images_data || [],  // Alt text data for each image
    gender: initialData?.gender || '',
    eye_color: initialData?.eye_color || '',
    hair_color: initialData?.hair_color || '',
    body_type: initialData?.body_type || '',
    height: initialData?.height || '',
    services: initialData?.services || [],
    seo: initialData?.seo || {
      meta_title: '',
      meta_description: '',
      keywords: [],
      slug: '',
      robots: 'index, follow',
      og_image: '',
      canonical_url: ''
    }
  });

  const [newSkill, setNewSkill] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [localError, setLocalError] = useState('');
  const [showSeoTab, setShowSeoTab] = useState(false);
  const [showImageAltModal, setShowImageAltModal] = useState(null);
  const [seoWarning, setSeoWarning] = useState(null);
  const [showSeoAlert, setShowSeoAlert] = useState(false);
  const [expandedTip, setExpandedTip] = useState(null);

  // Use the shared SEO score hook
  const liveScore = useListingSeoScore(formData, isAdmin);

  useEffect(() => {
    fetchAttributes();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        title: initialData.title || '',
        category: initialData.category || '',
        city: initialData.city || '',
        description: initialData.description || '',
        skills: initialData.skills || [],
        phone: initialData.phone || '',
        whatsapp: initialData.whatsapp || '',
        available: initialData.available ?? true,
        images: initialData.images || [],
        images_data: initialData.images_data || [],
        gender: initialData.gender || '',
        eye_color: initialData.eye_color || '',
        hair_color: initialData.hair_color || '',
        body_type: initialData.body_type || '',
        height: initialData.height || '',
        services: initialData.services || [],
        seo: initialData.seo || {
          meta_title: '',
          meta_description: '',
          keywords: [],
          slug: '',
          robots: 'index, follow',
          og_image: '',
          canonical_url: ''
        }
      });
    }
  }, [initialData]);

  const fetchAttributes = async () => {
    try {
      const response = await axios.get(`${API}/settings/attributes`);
      setAttributes(response.data);
    } catch (err) {
      console.error('Error fetching attributes:', err);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const remainingSlots = 10 - formData.images.length;
    if (remainingSlots <= 0) {
      alert('Maksimum 10 fotoğraf yükleyebilirsiniz.');
      return;
    }
    
    const filesToUpload = files.slice(0, remainingSlots);
    const newImages = [];
    
    for (const file of filesToUpload) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(`${API}/upload/image`, formDataUpload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        newImages.push(imageUrl);
      } catch (err) {
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
        const base64 = await base64Promise;
        newImages.push(base64);
      }
    }
    
    setFormData({ ...formData, images: [...formData.images, ...newImages] });
  };

  const removeImage = (indexToRemove) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, index) => index !== indexToRemove)
    });
  };

  const addSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData({ ...formData, skills: [...formData.skills, newSkill.trim()] });
      setNewSkill('');
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData({ 
      ...formData, 
      skills: formData.skills.filter(s => s !== skillToRemove) 
    });
  };

  const toggleService = (serviceId) => {
    if (formData.services.includes(serviceId)) {
      setFormData({
        ...formData,
        services: formData.services.filter(s => s !== serviceId)
      });
    } else {
      setFormData({
        ...formData,
        services: [...formData.services, serviceId]
      });
    }
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !formData.seo.keywords.includes(newKeyword.trim())) {
      setFormData({
        ...formData,
        seo: {
          ...formData.seo,
          keywords: [...formData.seo.keywords, newKeyword.trim()]
        }
      });
      setNewKeyword('');
    }
  };

  const removeKeyword = (keywordToRemove) => {
    setFormData({
      ...formData,
      seo: {
        ...formData.seo,
        keywords: formData.seo.keywords.filter(k => k !== keywordToRemove)
      }
    });
  };

  const updateSeoField = (field, value) => {
    setFormData({
      ...formData,
      seo: {
        ...formData.seo,
        [field]: value
      }
    });
  };

  const updateImageAltText = (index, altText, title = '') => {
    const newImagesData = [...formData.images_data];
    // Ensure array is long enough
    while (newImagesData.length <= index) {
      newImagesData.push({ url: formData.images[newImagesData.length] || '', alt_text: '', title: '' });
    }
    newImagesData[index] = {
      url: formData.images[index],
      alt_text: altText,
      title: title || newImagesData[index]?.title || ''
    };
    setFormData({ ...formData, images_data: newImagesData });
  };

  const getImageAltText = (index) => {
    if (formData.images_data && formData.images_data[index]) {
      return formData.images_data[index].alt_text || '';
    }
    return '';
  };

  const getImageTitle = (index) => {
    if (formData.images_data && formData.images_data[index]) {
      return formData.images_data[index].title || '';
    }
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError('');
    
    if (formData.images.length < 1) {
      setLocalError(t('listing.minPhotos'));
      return;
    }
    
    // Check SEO score for admin users
    if (isAdmin) {
      const { score, suggestions } = calculateListingSeoScore(formData);
      if (score < 50 && !showSeoAlert) {
        setSeoWarning({ score, suggestions });
        setShowSeoAlert(true);
        return; // Don't submit, show warning first
      }
    }
    
    const submitData = {
      ...formData,
      whatsapp: formData.whatsapp || formData.phone,
      gender: formData.gender || null,
      eye_color: formData.eye_color || null,
      hair_color: formData.hair_color || null,
      body_type: formData.body_type || null,
      height: formData.height || null
    };
    
    setShowSeoAlert(false);
    setSeoWarning(null);
    onSubmit(submitData);
  };

  const proceedWithLowScore = () => {
    setShowSeoAlert(false);
    const submitData = {
      ...formData,
      whatsapp: formData.whatsapp || formData.phone,
      gender: formData.gender || null,
      eye_color: formData.eye_color || null,
      hair_color: formData.hair_color || null,
      body_type: formData.body_type || null,
      height: formData.height || null
    };
    onSubmit(submitData);
  };

  const isValid = formData.name && formData.title && formData.category && 
                  formData.city && formData.description && formData.phone;

  const { categories, cities, genders, eye_colors: eyeColors, hair_colors: hairColors, 
          body_types: bodyTypes, heights, services } = attributes;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Live SEO Score Badge - Admin Only */}
      {isAdmin && (
        <div className={`rounded-lg border ${liveScore.getScoreBorderColor(liveScore.score)} bg-zinc-800/50`}>
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${liveScore.getScoreColor(liveScore.score)}`}>
                {liveScore.score}
              </div>
              <div>
                <p className="text-white font-medium text-sm">{t('seo.seoScore')}</p>
                <p className="text-zinc-500 text-xs">
                  {liveScore.score >= 70 ? t('seo.greatReadyToPublish') :
                   liveScore.score >= 50 ? t('seo.goodButCanImprove') :
                   t('seo.needsImprovement')}
                </p>
              </div>
            </div>
            {(liveScore.suggestions.length > 0 || liveScore.completed.length > 0) && (
              <button
                type="button"
                onClick={() => setExpandedTip(expandedTip ? null : 'main')}
                className="text-right hover:bg-zinc-700/50 p-2 rounded-lg transition-colors"
              >
                <p className="text-zinc-400 text-xs">
                  {liveScore.completed.length} {t('seo.completed').toLowerCase()}, {liveScore.suggestions.length} {t('seo.toImprove')}
                </p>
                <p className="text-emerald-400 text-xs max-w-[200px] truncate flex items-center gap-1">
                  {liveScore.suggestions.length > 0 ? liveScore.suggestions[0]?.title : 'All checks passed!'}
                  <svg className={`w-3 h-3 transition-transform ${expandedTip ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </p>
              </button>
            )}
          </div>
          
          {/* Expandable Tips Section */}
          {expandedTip && (
            <div className="border-t border-zinc-700 p-3 space-y-3">
              {/* Completed Items */}
              {liveScore.completed.length > 0 && (
                <div className="space-y-2">
                  <p className="text-emerald-400 text-xs font-medium flex items-center gap-1">
                    <Check className="w-3 h-3" /> {t('seo.completed')} ({liveScore.completed.length})
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {liveScore.completed.map((item, idx) => (
                      <div key={item.key || idx} className="flex items-center gap-2 text-sm">
                        <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-zinc-400 text-xs">{item.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Suggestions */}
              {liveScore.suggestions.length > 0 && (
                <div className="space-y-2">
                  <p className="text-red-400 text-xs font-medium flex items-center gap-1">
                    <X className="w-3 h-3" /> {t('seo.needsImprovement')} ({liveScore.suggestions.length})
                  </p>
                  {liveScore.suggestions.slice(0, 4).map((suggestion, idx) => (
                    <div key={suggestion.key || idx} className="bg-zinc-900/50 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setExpandedTip(expandedTip === suggestion.key ? 'main' : suggestion.key)}
                        className="w-full flex items-center justify-between p-2 hover:bg-zinc-700/30 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                            <X className="w-3 h-3 text-white" />
                          </div>
                          <span className="text-zinc-300 text-sm">{suggestion.title}</span>
                        </div>
                        <svg className={`w-4 h-4 text-zinc-500 transition-transform ${expandedTip === suggestion.key ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {expandedTip === suggestion.key && suggestion.tips && (
                        <div className="px-3 pb-3 space-y-1.5">
                          {suggestion.tips.map((tip, tipIdx) => (
                            <div key={tipIdx} className="flex items-start gap-2">
                              <span className="text-emerald-400 text-xs mt-0.5">•</span>
                              <span className="text-zinc-400 text-xs">{tip}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Basic Info */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-zinc-300">{t('listing.name')}</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white"
            placeholder={t('listing.enterName')}
            required
          />
        </div>

        <div className="space-y-2">
          <Label className="text-zinc-300">{t('listing.title')}</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white"
            placeholder={t('listing.enterTitle')}
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">{t('listing.category')}</Label>
            <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300">
                <SelectValue placeholder={t('listing.selectCategory')} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.name} className="text-zinc-300">
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">{t('listing.city')}</Label>
            <Select value={formData.city} onValueChange={(v) => setFormData({ ...formData, city: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300">
                <SelectValue placeholder={t('listing.selectCity')} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {cities.map(city => (
                  <SelectItem key={city.id} value={city.name} className="text-zinc-300">
                    {city.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Gender */}
        {genders.length > 0 && (
          <div className="space-y-2">
            <Label className="text-zinc-300">{t('physicalAttributes.gender')}</Label>
            <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300">
                <SelectValue placeholder={t('physicalAttributes.selectGender')} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {genders.map(g => (
                  <SelectItem key={g.id} value={g.id} className="text-zinc-300">
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Physical Attributes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {eyeColors?.length > 0 && (
          <div className="space-y-2">
            <Label className="text-zinc-300">{t('physicalAttributes.eyeColor')}</Label>
            <Select value={formData.eye_color} onValueChange={(v) => setFormData({ ...formData, eye_color: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {eyeColors.map(opt => (
                  <SelectItem key={opt.id} value={opt.id} className="text-zinc-300">
                    {opt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {hairColors?.length > 0 && (
          <div className="space-y-2">
            <Label className="text-zinc-300">{t('physicalAttributes.hairColor')}</Label>
            <Select value={formData.hair_color} onValueChange={(v) => setFormData({ ...formData, hair_color: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {hairColors.map(opt => (
                  <SelectItem key={opt.id} value={opt.id} className="text-zinc-300">
                    {opt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {bodyTypes?.length > 0 && (
          <div className="space-y-2">
            <Label className="text-zinc-300">{t('physicalAttributes.bodyType')}</Label>
            <Select value={formData.body_type} onValueChange={(v) => setFormData({ ...formData, body_type: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {bodyTypes.map(opt => (
                  <SelectItem key={opt.id} value={opt.id} className="text-zinc-300">
                    {opt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {heights?.length > 0 && (
          <div className="space-y-2">
            <Label className="text-zinc-300">{t('physicalAttributes.height')}</Label>
            <Select value={formData.height} onValueChange={(v) => setFormData({ ...formData, height: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {heights.map(opt => (
                  <SelectItem key={opt.id} value={opt.id} className="text-zinc-300">
                    {opt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Services */}
      {services?.length > 0 && (
        <div className="space-y-2">
          <Label className="text-zinc-300">{t('listing.servicesOffered')}</Label>
          <div className="flex flex-wrap gap-2">
            {services.map(s => (
              <Badge 
                key={s.id}
                className={`cursor-pointer transition-colors ${
                  formData.services.includes(s.id) 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:bg-zinc-700'
                }`}
                onClick={() => toggleService(s.id)}
              >
                {s.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      <div className="space-y-2">
        <Label className="text-zinc-300">{t('listing.aboutMe')}</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="bg-zinc-800 border-zinc-700 text-white min-h-32"
          placeholder={t('listing.aboutMePlaceholder', 'Kendiniz, deneyiminiz ve uzmanlık alanlarınız hakkında bilgi verin...')}
          required
        />
      </div>

      {/* Skills */}
      <div className="space-y-2">
        <Label className="text-zinc-300">{t('listing.skills')}</Label>
        <div className="flex gap-2">
          <Input
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
            className="bg-zinc-800 border-zinc-700 text-white flex-1"
            placeholder={t('listing.addSkill')}
          />
          <Button type="button" onClick={addSkill} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        {formData.skills.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {formData.skills.map((skill, idx) => (
              <Badge key={idx} className="bg-zinc-800 text-zinc-300 border-zinc-700 pr-1">
                {skill}
                <button 
                  type="button"
                  onClick={() => removeSkill(skill)}
                  className="ml-2 hover:text-red-400"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Contact */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-zinc-300">{t('listing.phone')}</Label>
          <Input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white"
            placeholder="+1 555 123 4567"
            required
          />
        </div>

        <div className="space-y-2">
          <Label className="text-zinc-300">{t('listing.whatsappOptional')}</Label>
          <Input
            type="tel"
            value={formData.whatsapp}
            onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white"
            placeholder="+1 555 123 4567"
          />
        </div>
      </div>

      {/* Images */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-300">{t('listing.photos')}</Label>
          <span className="text-zinc-500 text-sm">{formData.images.length}/10</span>
        </div>
        
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 sm:gap-3">
          {formData.images.map((img, index) => (
            <div key={index} className="relative aspect-square group">
              <img 
                src={img} 
                alt={getImageAltText(index) || `Photo ${index + 1}`} 
                className="w-full h-full rounded-lg object-cover"
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 hover:bg-red-600 transition-colors z-10"
              >
                <X className="w-3 h-3 text-white" />
              </button>
              {index === 0 && (
                <span className="absolute bottom-1 left-1 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded">
                  {t('listing.mainPhoto')}
                </span>
              )}
              {/* Alt text edit button - Admin only */}
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => setShowImageAltModal(index)}
                  className={`absolute bottom-1 right-1 px-2 py-0.5 rounded text-xs transition-all ${
                    getImageAltText(index) 
                      ? 'bg-emerald-500/80 text-white' 
                      : 'bg-yellow-500/80 text-white opacity-0 group-hover:opacity-100'
                  }`}
                  title="Edit Alt Text"
                >
                  {getImageAltText(index) ? 'ALT ✓' : 'ALT'}
                </button>
              )}
            </div>
          ))}
          
          {formData.images.length < 10 && (
            <label className="aspect-square rounded-lg border-2 border-dashed border-zinc-700 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 transition-colors">
              <Upload className="w-5 h-5 text-zinc-500 mb-1" />
              <span className="text-zinc-500 text-xs">{t('listing.addPhoto')}</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          )}
        </div>
        
        <p className="text-zinc-500 text-sm">
          {t('listing.photoFormats')}
        </p>
        
        {formData.images.length < 1 && (
          <p className="text-red-400 text-sm">* {t('listing.minPhotos')}</p>
        )}
      </div>

      {/* Image Alt Text Modal */}
      {showImageAltModal !== null && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowImageAltModal(null)}>
          <div className="bg-zinc-900 rounded-xl p-6 max-w-md w-full mx-4 border border-zinc-800" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-medium mb-4">{t('seo.seoSettings', 'Görsel SEO Ayarları')}</h3>
            <div className="flex gap-4 mb-4">
              <img 
                src={formData.images[showImageAltModal]} 
                alt="Preview" 
                className="w-24 h-24 rounded-lg object-cover"
              />
              <div className="flex-1 space-y-3">
                <div>
                  <Label className="text-zinc-400 text-sm">Alt Text</Label>
                  <Input
                    value={getImageAltText(showImageAltModal)}
                    onChange={(e) => updateImageAltText(showImageAltModal, e.target.value, getImageTitle(showImageAltModal))}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    placeholder={t('seo.describeImage')}
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-sm">Title</Label>
                  <Input
                    value={getImageTitle(showImageAltModal)}
                    onChange={(e) => updateImageAltText(showImageAltModal, getImageAltText(showImageAltModal), e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    placeholder="Image title"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowImageAltModal(null)} className="border-zinc-700 text-zinc-300">
                {t('common.close')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* SEO Settings - Admin Only */}
      {isAdmin && (
        <div className="border border-zinc-800 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowSeoTab(!showSeoTab)}
            className="w-full flex items-center justify-between p-4 bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-white font-medium">{t('seo.seoSettings')}</p>
                <p className="text-zinc-500 text-sm">{t('seo.metaTagsDesc', 'Meta etiketleri, anahtar kelimeler ve arama optimizasyonu')}</p>
              </div>
            </div>
            <svg className={`w-5 h-5 text-zinc-400 transition-transform ${showSeoTab ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showSeoTab && (
            <div className="p-4 space-y-4 bg-zinc-900/50">
              {/* Meta Title */}
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('seo.metaTitle')}</Label>
                <Input
                  value={formData.seo?.meta_title || ''}
                  onChange={(e) => updateSeoField('meta_title', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder={t('seo.customSeoTitle')}
                />
                <p className="text-zinc-500 text-xs">{(formData.seo?.meta_title || '').length}/60 {t('seo.charsCount')}</p>
              </div>

              {/* Meta Description */}
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('seo.metaDescription')}</Label>
                <Textarea
                  value={formData.seo?.meta_description || ''}
                  onChange={(e) => updateSeoField('meta_description', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder={t('seo.customMetaDesc')}
                  rows={3}
                />
                <p className="text-zinc-500 text-xs">{(formData.seo?.meta_description || '').length}/160 {t('seo.charsCount')}</p>
              </div>

              {/* Keywords */}
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('seo.seoKeywords')}</Label>
                <div className="flex gap-2">
                  <Input
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                    className="bg-zinc-800 border-zinc-700 text-white flex-1"
                    placeholder={t('seo.addKeyword')}
                  />
                  <Button type="button" onClick={addKeyword} className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.seo?.keywords?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.seo.keywords.map((keyword, idx) => (
                      <Badge key={idx} className="bg-purple-500/20 text-purple-300 border border-purple-500/30 pr-1">
                        {keyword}
                        <button 
                          type="button"
                          onClick={() => removeKeyword(keyword)}
                          className="ml-2 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-zinc-500 text-xs">{t('seo.keywordsHelp')}</p>
              </div>

              {/* Custom Slug */}
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('seo.customSlug')}</Label>
                <Input
                  value={formData.seo?.slug || ''}
                  onChange={(e) => updateSeoField('slug', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="custom-url-slug"
                />
              </div>

              {/* Robots */}
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('seo.robotsMeta')}</Label>
                <Select 
                  value={formData.seo?.robots || 'index, follow'}
                  onValueChange={(v) => updateSeoField('robots', v)}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="index, follow" className="text-zinc-300">Index, Follow</SelectItem>
                    <SelectItem value="index, nofollow" className="text-zinc-300">Index, NoFollow</SelectItem>
                    <SelectItem value="noindex, follow" className="text-zinc-300">NoIndex, Follow</SelectItem>
                    <SelectItem value="noindex, nofollow" className="text-zinc-300">NoIndex, NoFollow</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* OG Image */}
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('seo.ogImage')}</Label>
                <Input
                  value={formData.seo?.og_image || ''}
                  onChange={(e) => updateSeoField('og_image', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder={t('seo.ogImageLeaveEmpty')}
                />
              </div>

              {/* Canonical URL */}
              <div className="space-y-2">
                <Label className="text-zinc-300">{t('seo.canonicalUrl')}</Label>
                <Input
                  value={formData.seo?.canonical_url || ''}
                  onChange={(e) => updateSeoField('canonical_url', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder={t('seo.canonicalUrlLeaveEmpty')}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {(error || localError) && <p className="text-red-400 text-sm">{error || localError}</p>}

      {/* SEO Score Warning Alert */}
      {showSeoAlert && seoWarning && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowSeoAlert(false)}>
          <div className="bg-zinc-900 rounded-xl p-6 max-w-lg w-full mx-4 border border-yellow-500/30" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">{t('seo.lowSeoWarning')}</h3>
                <p className="text-zinc-400 text-sm mt-1">
                  This listing has an SEO score of <span className="text-yellow-400 font-bold">{seoWarning.score}/100</span>
                </p>
              </div>
            </div>
            
            <div className="bg-zinc-800/50 rounded-lg p-4 mb-4">
              <p className="text-zinc-300 text-sm font-medium mb-2">{t('seo.suggestionsToImprove')}</p>
              <ul className="space-y-2">
                {seoWarning.suggestions.slice(0, 5).map((suggestion, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-zinc-400">
                    <span className="text-yellow-400 mt-0.5">•</span>
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="flex gap-3">
              <Button 
                type="button"
                onClick={() => setShowSeoAlert(false)}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {t('seo.improveSeo')}
              </Button>
              <Button 
                type="button"
                onClick={proceedWithLowScore}
                variant="outline"
                className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                {t('seo.saveAnyway')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Button 
        type="submit"
        disabled={!isValid || submitting}
        className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 py-6 text-lg"
      >
        {submitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            {loadingButtonText}
          </>
        ) : (
          submitButtonText
        )}
      </Button>
    </form>
  );
};

export default ListingForm;
